import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface DecorativeQrProps {
    seed?: number
    className?: string
}

const SIZE = 21

const isFinder = (x: number, y: number): boolean => {
    const inBox = (bx: number, by: number) => x >= bx && x < bx + 7 && y >= by && y < by + 7
    return inBox(0, 0) || inBox(SIZE - 7, 0) || inBox(0, SIZE - 7)
}

const finderCell = (x: number, y: number): boolean => {
    const local = (v: number) => (v >= SIZE - 7 ? v - (SIZE - 7) : v)
    const lx = local(x)
    const ly = local(y)
    if (lx === 0 || lx === 6 || ly === 0 || ly === 6) return true
    return lx >= 2 && lx <= 4 && ly >= 2 && ly <= 4
}

const buildCells = (seed: number): Array<[number, number]> => {
    const cells: Array<[number, number]> = []
    let state = Math.max(1, seed)

    for (let y = 0; y < SIZE; y++) {
        for (let x = 0; x < SIZE; x++) {
            state = (state * 16807) % 2147483647
            const on = isFinder(x, y) ? finderCell(x, y) : state % 100 < 48
            if (on) cells.push([x, y])
        }
    }

    return cells
}

export const DecorativeQr: FC<DecorativeQrProps> = ({ seed = 7, className }): ReactNode => {
    const cells = buildCells(seed)

    return (
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className={cn('size-24', className)} aria-hidden='true' shapeRendering='crispEdges'>
            <rect width={SIZE} height={SIZE} fill='white' />
            {cells.map(([x, y]) => (
                <rect key={`${x}-${y}`} x={x} y={y} width='1' height='1' fill='currentColor' />
            ))}
        </svg>
    )
}
